import { useMemo, useState } from "react";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import { money, type Money } from "../../domain/money/money";
import { tenderState, quickCashOptions, type TenderEntry } from "../../domain/journal/tender";
import { uuidv7 } from "../../platform/id";

/**
 * Taking payment.
 *
 * Nothing on this path awaits the network. The sale commits locally and the UI
 * confirms from that commit (Principle I, FR-043), so this screen behaves
 * identically with the internet unplugged.
 *
 * Split tenders accumulate: each one reduces the balance until it settles.
 */

interface TenderSheetProps {
  due: Money;
  currency: CurrencyFormat;
  tenderTypes: string[];
  tenderLabels: Record<string, string>;
  busy?: boolean;
  onComplete(tenders: TenderEntry[]): void;
  onCancel(): void;
}

export function TenderSheet({
  due,
  currency,
  tenderTypes,
  tenderLabels,
  busy = false,
  onComplete,
  onCancel,
}: TenderSheetProps) {
  const [tenders, setTenders] = useState<TenderEntry[]>([]);
  const [activeType, setActiveType] = useState<string>(tenderTypes[0] ?? "cash");
  const [entry, setEntry] = useState("");
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);

  const state = useMemo(() => {
    try {
      return tenderState(due, tenders);
    } catch {
      return null;
    }
  }, [due, tenders]);

  const remaining = state?.remaining ?? due;
  const quick = useMemo(() => quickCashOptions(remaining), [remaining]);

  const addTender = (amount: number, type: string, ref: string | null) => {
    if (amount <= 0) {
      setError("Enter an amount");
      return;
    }
    const next: TenderEntry[] = [
      ...tenders,
      { id: uuidv7(), type, amount: money(amount), reference: ref },
    ];
    try {
      tenderState(due, next);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Invalid tender");
      return;
    }
    setError(null);
    setTenders(next);
    setEntry("");
    setReference("");
  };

  const pressDigit = (digit: string) => {
    setError(null);
    setEntry((current) => (current + digit).replace(/^0+(?=\d)/, "").slice(0, 9));
  };

  const entryAmount = entry === "" ? 0 : Number.parseInt(entry, 10);
  const isCash = activeType === "cash";

  return (
    <div className="tender" role="dialog" aria-label="Take payment">
      <header className="tender__head">
        <div className="tender__due">
          <span className="tender__due-label">
            {/* A sale its discounts paid for in full has nothing to tender
                (a free item, a 100% discount) — "Change ₱0.00" would read as
                if money had changed hands. */}
            {due === 0 ? "Nothing to pay" : state?.settled ? "Change" : "Balance due"}
          </span>
          <span className="tender__due-amount money">
            {formatMoney(state?.settled ? state.change : remaining, currency)}
          </span>
        </div>
        {tenders.length > 0 && (
          <ul className="tender__list">
            {tenders.map((tender) => (
              <li key={tender.id}>
                <span>{tenderLabels[tender.type] ?? tender.type}</span>
                <span className="money">{formatMoney(tender.amount, currency)}</span>
              </li>
            ))}
          </ul>
        )}
      </header>

      {!state?.settled && (
        <>
          <div className="tender__types" role="group" aria-label="Payment method">
            {tenderTypes.map((type) => (
              <button
                key={type}
                type="button"
                className={`tender__type${type === activeType ? " is-selected" : ""}`}
                onClick={() => {
                  setActiveType(type);
                  setError(null);
                }}
                aria-pressed={type === activeType}
              >
                {tenderLabels[type] ?? type}
              </button>
            ))}
          </div>

          {isCash && quick.length > 0 && (
            <div className="tender__quick">
              {quick.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className="tender__quick-option"
                  onClick={() => addTender(amount, "cash", null)}
                >
                  {formatMoney(amount as Money, currency)}
                </button>
              ))}
            </div>
          )}

          <div className="tender__entry">
            <span className="tender__entry-value money">
              {formatMoney(entryAmount as Money, currency)}
            </span>
          </div>

          {!isCash && (
            <input
              className="tender__reference"
              placeholder="Reference number"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              inputMode="text"
            />
          )}

          <div className="tender__keys">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((key) => (
              <button key={key} type="button" className="tender__key" onClick={() => pressDigit(key)}>
                {key}
              </button>
            ))}
            <button type="button" className="tender__key" onClick={() => pressDigit("00")}>
              00
            </button>
            <button type="button" className="tender__key" onClick={() => pressDigit("0")}>
              0
            </button>
            <button
              type="button"
              className="tender__key tender__key--muted"
              onClick={() => setEntry((c) => c.slice(0, -1))}
              aria-label="Delete"
            >
              ⌫
            </button>
          </div>

          <button
            type="button"
            className="tender__apply"
            disabled={entryAmount <= 0}
            onClick={() => addTender(entryAmount, activeType, isCash ? null : reference || null)}
          >
            Add {tenderLabels[activeType] ?? activeType}
          </button>
        </>
      )}

      {error && (
        <p className="tender__error" role="alert">
          {error}
        </p>
      )}

      <footer className="tender__foot">
        <button type="button" className="tender__cancel" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="tender__complete"
          disabled={!state?.settled || busy}
          onClick={() => onComplete(tenders)}
        >
          {busy ? "Recording…" : "Complete sale"}
        </button>
      </footer>
    </div>
  );
}

export default TenderSheet;
